import DashboardLayout from '@/components/DashboardLayout'
import { router, Link } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Download, Receipt, AlertTriangle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface Teacher {
  id: number
  name: string
}

export interface Row {
  id: number
  lesson_date: string
  start_time: string
  end_time: string
  label: string
  teacher: { id: number; name: string } | null
  students: string[]
  students_count: number
  payable_students_count: number
  pending_students_count: number
  present_count: number
  absent_count: number
  unit_teacher_payment: number
  amount: number
  payment_state: 'paid' | 'launched' | 'unpaid'
}

interface Summary {
  lessons_count: number
  students_count: number
  payable_students_count: number
  pending_students_count: number
  total_amount: number
  paid_amount: number
  unpaid_amount: number
}

interface ByTeacher {
  teacher_id: number | null
  teacher_name: string
  lessons_count: number
  payable_students_count: number
  pending_students_count: number
  total_amount: number
}

interface Filters {
  start_date: string
  end_date: string
  teacher_id?: string
  attendance: string
}

interface Props {
  rows: Row[]
  summary: Summary
  by_teacher: ByTeacher[]
  filters: Filters
  teachers: Teacher[]
}

const TEACHER_ALL = '__all__'

const ATTENDANCE_OPTIONS = [
  { value: 'payable', label: 'Pagáveis (presente ou faltou)' },
  { value: 'all', label: 'Todas' },
  { value: 'present', label: 'Só confirmadas (presente)' },
  { value: 'absent', label: 'Só faltas' },
  { value: 'pending', label: 'Só pendentes' },
]

const PAYMENT_LABELS: Record<Row['payment_state'], string> = {
  paid: 'Pago',
  launched: 'Lançado',
  unpaid: 'Não lançado',
}

const money = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const shortDate = (iso: string) =>
  format(new Date(iso + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })

const fmt = (d: Date) => format(d, 'yyyy-MM-dd')

// Presets do período. O default do relatório é o mês corrente — a pergunta que a
// escola faz ao abrir a tela.
const presetRange = (key: string): { start: string; end: string } | null => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  switch (key) {
    case 'today':
      return { start: fmt(today), end: fmt(today) }
    case 'last_7': {
      const s = new Date(today)
      s.setDate(s.getDate() - 6)
      return { start: fmt(s), end: fmt(today) }
    }
    case 'this_month':
      return {
        start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
        end: fmt(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      }
    case 'last_month':
      return {
        start: fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        end: fmt(new Date(today.getFullYear(), today.getMonth(), 0)),
      }
    default:
      return null
  }
}

export default function LessonsGiven({
  rows,
  summary,
  by_teacher,
  filters,
  teachers,
}: Props) {
  const [startDate, setStartDate] = useState(filters.start_date || '')
  const [endDate, setEndDate] = useState(filters.end_date || '')
  const [teacherId, setTeacherId] = useState(filters.teacher_id || TEACHER_ALL)
  const [attendance, setAttendance] = useState(filters.attendance || 'payable')
  const [selected, setSelected] = useState<number[]>([])

  const selectedSet = useMemo(() => new Set(selected), [selected])
  // Uma linha só entra no total selecionado se ainda estiver na lista atual —
  // trocar de filtro pode ter tirado da tela algo que estava marcado.
  const visibleSelected = useMemo(
    () => rows.filter((row) => selectedSet.has(row.id)),
    [rows, selectedSet],
  )
  const selectedAmount = visibleSelected.reduce(
    (sum, row) => sum + row.amount,
    0,
  )
  const allSelected = rows.length > 0 && visibleSelected.length === rows.length

  const queryParams = () => {
    const params = new URLSearchParams()
    if (startDate) params.set('start_date', startDate)
    if (endDate) params.set('end_date', endDate)
    if (teacherId !== TEACHER_ALL) params.set('teacher_id', teacherId)
    params.set('attendance', attendance)
    return params
  }

  const applyFilters = (range?: { start: string; end: string }) => {
    if (range) {
      setStartDate(range.start)
      setEndDate(range.end)
    }
    router.get(
      '/reports/lessons_given',
      {
        start_date: range ? range.start : startDate || undefined,
        end_date: range ? range.end : endDate || undefined,
        teacher_id: teacherId === TEACHER_ALL ? undefined : teacherId,
        attendance,
      },
      { preserveState: true, preserveScroll: true },
    )
  }

  const applyPreset = (key: string) => {
    const range = presetRange(key)
    if (range) applyFilters(range)
  }

  const exportPdf = () => {
    const params = queryParams()
    // Seleção parcial vai como lista de ids; tudo selecionado (ou nada) manda só
    // os filtros, o que também evita uma URL quilométrica em períodos longos.
    if (visibleSelected.length > 0 && !allSelected) {
      params.set('lesson_ids', visibleSelected.map((row) => row.id).join(','))
    }
    window.location.href = `/reports/lessons_given.pdf?${params.toString()}`
  }

  const toggleRow = (id: number, checked: boolean) => {
    setSelected((current) =>
      checked ? [...current, id] : current.filter((rowId) => rowId !== id),
    )
  }

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? rows.map((row) => row.id) : [])
  }

  const activePreset = ['today', 'last_7', 'this_month', 'last_month'].find(
    (key) => {
      const range = presetRange(key)
      return range && range.start === startDate && range.end === endDate
    },
  )

  const exportLabel =
    visibleSelected.length > 0 && !allSelected
      ? `Exportar PDF (${visibleSelected.length} selecionada${visibleSelected.length > 1 ? 's' : ''})`
      : 'Exportar PDF'

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Aulas Dadas</h1>
            <p className="text-sm text-muted-foreground">
              Quanto cada professor produziu no período. O valor de cada aula
              conta só os alunos com presença resolvida — presente ou faltou.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" asChild>
              <Link href="/teacher_payments">
                <Receipt className="w-4 h-4 mr-2" /> Lançar pagamento
              </Link>
            </Button>
            <Button onClick={exportPdf} disabled={rows.length === 0}>
              <Download className="w-4 h-4 mr-2" /> {exportLabel}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-emerald-600">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Total a pagar
              </div>
              <div className="text-3xl font-bold mt-1 text-emerald-700">
                {money(summary.total_amount)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {money(summary.unpaid_amount)} ainda não pago
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Aulas dadas
              </div>
              <div className="text-3xl font-bold mt-1">
                {summary.lessons_count}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary.payable_students_count} aluno(s) contabilizado(s)
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Já pago
              </div>
              <div className="text-3xl font-bold mt-1">
                {money(summary.paid_amount)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                repasses já fechados no período
              </div>
            </CardContent>
          </Card>
          <Card
            className={
              summary.pending_students_count > 0
                ? 'border-l-4 border-l-amber-500'
                : ''
            }
          >
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Presenças pendentes
              </div>
              <div
                className={`text-3xl font-bold mt-1 ${summary.pending_students_count > 0 ? 'text-amber-600' : ''}`}
              >
                {summary.pending_students_count}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                aluno(s) que ainda não somam no total
              </div>
            </CardContent>
          </Card>
        </div>

        {summary.pending_students_count > 0 && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              {summary.pending_students_count} aluno(s) ainda estão com presença
              pendente e não entraram no total. O valor dessas aulas sobe quando
              alguém marcar — resolva antes de fechar o repasse, ou o professor
              fica credor da diferença. Use o filtro{' '}
              <strong>Só pendentes</strong> para achá-las.
            </p>
          </div>
        )}

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-1.5 mb-4">
              {[
                { key: 'today', label: 'Hoje' },
                { key: 'last_7', label: 'Últimos 7 dias' },
                { key: 'this_month', label: 'Mês atual' },
                { key: 'last_month', label: 'Mês passado' },
              ].map((preset) => (
                <Button
                  key={preset.key}
                  size="sm"
                  variant={activePreset === preset.key ? 'default' : 'outline'}
                  onClick={() => applyPreset(preset.key)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">De</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Até</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Professor</Label>
                <Select value={teacherId} onValueChange={setTeacherId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TEACHER_ALL}>Todos</SelectItem>
                    {teachers.map((teacher) => (
                      <SelectItem
                        key={teacher.id}
                        value={teacher.id.toString()}
                      >
                        {teacher.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Presença</Label>
                <Select value={attendance} onValueChange={setAttendance}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ATTENDANCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => applyFilters()}>Aplicar filtros</Button>
            </div>
          </CardContent>
        </Card>

        {by_teacher.length > 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Total por professor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {by_teacher.map((teacher) => (
                  <div
                    key={teacher.teacher_id ?? 'sem-professor'}
                    className="flex items-center justify-between gap-3 border-b last:border-0 pb-2 last:pb-0"
                  >
                    <div className="text-sm font-medium truncate">
                      {teacher.teacher_name}
                    </div>
                    <div className="flex items-center gap-4 text-sm shrink-0">
                      <span className="text-muted-foreground">
                        {teacher.lessons_count} aula(s)
                      </span>
                      {teacher.pending_students_count > 0 && (
                        <span className="text-amber-600">
                          {teacher.pending_students_count} pendente(s)
                        </span>
                      )}
                      <span className="font-semibold w-28 text-right">
                        {money(teacher.total_amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              Aulas de {shortDate(filters.start_date)} a{' '}
              {shortDate(filters.end_date)}
            </CardTitle>
            {visibleSelected.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {visibleSelected.length} selecionada(s) ·{' '}
                <strong className="text-foreground">
                  {money(selectedAmount)}
                </strong>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">
                Nenhuma aula dada neste período com os filtros escolhidos.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(checked) =>
                            toggleAll(checked === true)
                          }
                          aria-label="Selecionar todas as aulas"
                        />
                      </TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Horário</TableHead>
                      <TableHead>Professor</TableHead>
                      <TableHead>Alunos</TableHead>
                      <TableHead>Presença</TableHead>
                      <TableHead>Repasse</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-testid={`lesson-row-${row.id}`}
                        className={selectedSet.has(row.id) ? 'bg-muted/50' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedSet.has(row.id)}
                            onCheckedChange={(checked) =>
                              toggleRow(row.id, checked === true)
                            }
                            aria-label={`Selecionar aula de ${shortDate(row.lesson_date)}`}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {shortDate(row.lesson_date)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {row.start_time}-{row.end_time}
                        </TableCell>
                        <TableCell>{row.teacher?.name || '-'}</TableCell>
                        <TableCell className="max-w-[220px] truncate">
                          {row.students.join(', ') || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {row.present_count > 0 && (
                              <Badge variant="secondary">
                                {row.present_count} presente
                              </Badge>
                            )}
                            {row.absent_count > 0 && (
                              <Badge variant="outline">
                                {row.absent_count} faltou
                              </Badge>
                            )}
                            {row.pending_students_count > 0 && (
                              <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-200">
                                {row.pending_students_count} pendente
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              row.payment_state === 'paid'
                                ? 'default'
                                : 'outline'
                            }
                          >
                            {PAYMENT_LABELS[row.payment_state]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="font-semibold">
                            {money(row.amount)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {money(row.unit_teacher_payment)} ×{' '}
                            {row.payable_students_count}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
